import {
  type RegistrationForm,
  type RegistrationFormField,
  type RegistrationFormFieldDescription,
  type RegistrationFormFieldDraft,
  type RegistrationFormFieldKey,
  type RegistrationFormFieldType,
} from '@/Types/RegistrationForm'
import {pluralize} from '@/Utils/Interface'

/**
 * Display name for a registration form, falling back to a sensible default when
 * a freshly created draft has no name yet.
 */
export function registrationFormDisplayName (form: Pick<RegistrationForm, 'name'>): string {
  const trimmed = form.name?.trim() ?? ''

  return trimmed.length > 0 ? trimmed : 'Untitled form'
}

/**
 * Selectable field types, in the order they appear in the type picker. The
 * labels are operator-facing.
 */
export const REGISTRATION_FIELD_TYPE_OPTIONS: Array<{value: RegistrationFormFieldType, label: string}> = [
  {value: 'text', label: 'Short text'},
  {value: 'textarea', label: 'Paragraph'},
  {value: 'dropdown', label: 'Dropdown'},
  {value: 'radio', label: 'Multiple choice'},
  {value: 'checkbox', label: 'Checkboxes'},
]

/**
 * The validation keys each type can actually use. Mirrors
 * `RegistrationFormFieldType::supportedValidationKeys()` on the backend so the
 * builder only ever offers — and sends — meaningful rules.
 */
export const SUPPORTED_VALIDATION_KEYS: Record<RegistrationFormFieldType, Array<keyof RegistrationFormFieldDraft['validations']>> = {
  text: ['required', 'min_length', 'max_length'],
  textarea: ['required', 'min_length', 'max_length'],
  dropdown: ['required'],
  radio: ['required'],
  checkbox: ['required', 'min_selected', 'max_selected'],
}

/**
 * Whether a field type presents operator-authored choices (and persists options).
 * Mirrors `RegistrationFormFieldType::hasOptions()`.
 */
export function fieldTypeHasOptions (type: RegistrationFormFieldType): boolean {
  return type === 'dropdown' || type === 'radio' || type === 'checkbox'
}

export function fieldTypeSupportsValidation (
  type: RegistrationFormFieldType,
  key: keyof RegistrationFormFieldDraft['validations'],
): boolean {
  return SUPPORTED_VALIDATION_KEYS[type].includes(key)
}

/**
 * Look up an option's label by value, falling back to the raw value itself.
 * Shared by every `{value, label}` picker list in this module.
 */
function labelFromOptions<Value extends string> (options: Array<{value: Value, label: string}>, value: Value): string {
  return options.find((option) => option.value === value)?.label ?? value
}

export function registrationFieldTypeLabel (type: RegistrationFormFieldType): string {
  return labelFromOptions(REGISTRATION_FIELD_TYPE_OPTIONS, type)
}

export type RegistrationFormFieldValue = string | boolean | string[] | undefined

/**
 * Whether a single field's current value satisfies its own `required` rule.
 * Non-required fields always pass. Checks "required" only — `min_selected`/
 * `min_length`/etc. range rules are left to the server, which already
 * enforces and tests them (ENG-3919).
 */
export function isCustomFieldComplete (field: RegistrationFormField, value: RegistrationFormFieldValue): boolean {
  if (field.validations?.required !== true) {
    return true
  }

  if (field.type === 'checkbox') {
    return Array.isArray(value) && value.length > 0
  }

  return typeof value === 'string' && value.trim() !== ''
}

/**
 * Whether every required custom question currently has a value, keyed the
 * same way the public registration form stores them (`custom_{field.id}`).
 */
export function customFieldsComplete (fields: RegistrationFormField[], values: Record<string, RegistrationFormFieldValue>): boolean {
  return fields.every((field) => isCustomFieldComplete(field, values[`custom_${field.id}`]))
}

/**
 * Label text with a required-marker suffix. There's no shared "required
 * asterisk" convention in this codebase — `Input`/`SelectDropdown`'s
 * `title`/`label` props are plain `string`, not `ReactNode` — so this stays
 * a simple string transform rather than a new component.
 */
export function customFieldDisplayLabel (field: Pick<RegistrationFormField, 'label' | 'validations'>): string {
  return field.validations?.required === true ? `${field.label} *` : field.label
}

/**
 * One-line summary for a collapsed, completed Coach section.
 */
export function coachSummary (values: {coach_first_name?: string, coach_last_name?: string, coach_email?: string}): string {
  const name = `${values.coach_first_name ?? ''} ${values.coach_last_name ?? ''}`.trim()
  const email = values.coach_email ?? ''

  if (name !== '' && email !== '') {
    return `${name} — ${email}`
  }

  return name !== '' ? name : email
}

/**
 * A Sport open for registration on the form, with the Ages and
 * Classifications it offers — the shape of the page's `team_categories` prop.
 */
export type PrefillCategory = {
  id: string
  ages: {id: string}[]
  classifications: {id: string}[]
}

/**
 * The classification id to auto-select on the Team step, once an Age is
 * chosen and the Sport offers exactly one Classification (ENG-4263).
 * Gated on Age so a sole Classification cannot complete the step before
 * the coach has picked one (ENG-4279). Returns null for zero or multiple
 * options, or when Age has not been selected yet.
 */
export function soleTeamClassificationId (
  category: PrefillCategory | null | undefined,
  ageId: string,
): string | null {
  if (ageId === '' || category == null || category.classifications.length !== 1) {
    return null
  }

  return category.classifications[0].id
}

/**
 * The classification id currently selected on the Team step — the coach's
 * explicit pick if any, otherwise the Sport's sole Classification once
 * Age is chosen (ENG-4263, gated by ENG-4279). Resolved at read time so
 * the select, the summary, and the submit payload agree without writing
 * the auto-select into form state.
 */
export function resolvedTeamClassificationId (
  category: PrefillCategory | null | undefined,
  ageId: string,
  classificationId: string,
): string {
  return classificationId || soleTeamClassificationId(category, ageId) || ''
}

/**
 * Whether the Team step's Continue control may fire. Age is always
 * required (tournament eligibility is empty without it). Classification
 * is required only when the Sport offers any; a sole Classification
 * auto-selects after Age and counts as chosen.
 */
export function teamStepFieldsComplete ({
  coachPersonId,
  teamName,
  categoryId,
  ageId,
  classificationId,
  category,
}: {
  coachPersonId: string | null
  teamName: string
  categoryId: string
  ageId: string
  classificationId: string
  category: PrefillCategory | null | undefined
}): boolean {
  if (coachPersonId == null || teamName === '' || categoryId === '' || ageId === '' || category == null) {
    return false
  }

  if (category.classifications.length === 0) {
    return true
  }

  return resolvedTeamClassificationId(category, ageId, classificationId) !== ''
}

/**
 * One-line summary for a collapsed, completed Team section. Age is required;
 * classification is omitted when the selected sport offers none, so blank
 * ones are dropped rather than shown as empty segments.
 */
export function teamSummary (
  teamName: string,
  categoryName?: string | null,
  ageName?: string | null,
  classificationName?: string | null,
): string {
  const details = [categoryName, ageName, classificationName]
    .filter((detail): detail is string => detail != null && detail !== '')

  return details.length > 0 ? `${teamName} — ${details.join(', ')}` : teamName
}

export type PrefillTeam = {
  team_name: string
  team_category_id: string | null
  team_age_id: string | null
  team_classification_id: string | null
}

export type PrefillAddress = {
  address_street_1: string | null
  address_street_2: string | null
  address_locality: string | null
  address_region: string | null
  address_postal_code: string | null
  address_country: string | null
}

type PrefillValues = Record<string, string | boolean | string[]>

function isOffered (options: {id: string}[], id: string | null): id is string {
  return id != null && options.some((option) => option.id === id)
}

/**
 * The stored Sport/Age/Classification ids worth writing into the form, given
 * the Sport the form will end up on: the one already on screen, else the
 * team's own. A stored Age or Classification is dropped when that Sport does
 * not offer it, and a stored Sport is dropped (along with both) when the form
 * does not offer it at all (ENG-4282).
 *
 * Without this, a single-Sport org preselects the Sport on load and the
 * prefill then copies last season's Classification from a different Sport
 * on top of it. The native select can't show an id that isn't one of its
 * options, and when the Sport has a sole Classification the select is also
 * disabled (after Age is chosen, ENG-4279), so the coach is left with a
 * blank, locked field and a server rejection they cannot act on. Dropping
 * the id lets the sole-classification auto-select (ENG-4263) take over
 * instead once Age is selected.
 */
function offeredTeamIds (
  values: PrefillValues,
  team: PrefillTeam,
  categories: PrefillCategory[],
): PrefillValues {
  const onScreenCategoryId = typeof values.team_category_id === 'string' ? values.team_category_id : ''
  const category = categories.find((option) => option.id === (onScreenCategoryId || team.team_category_id))

  if (category == null) {
    return {}
  }

  return {
    ...(onScreenCategoryId ? {} : {team_category_id: category.id}),
    ...(!values.team_age_id && isOffered(category.ages, team.team_age_id) ? {team_age_id: team.team_age_id} : {}),
    ...(!values.team_classification_id && isOffered(category.classifications, team.team_classification_id)
      ? {team_classification_id: team.team_classification_id}
      : {}),
  }
}

/**
 * Merges the Team + Address a verified returning coach already has on file
 * into the public form's current values (ENG-3861, opted into via the Yes/No
 * offer added by ENG-4059).
 *
 * Each field is written only when it's still empty, so anything already on
 * screen wins — a single-category org preselects the Sport on load, for one.
 * Sport/Age/Classification ids are additionally written only when the form
 * actually offers them (see offeredTeamIds, ENG-4282). Address is
 * all-or-nothing off `address_street_1` rather than field-by-field: a
 * half-typed address means the coach is already entering one, and threading
 * stored parts through it would produce a hybrid matching neither.
 */
export function applyPrefill (
  values: PrefillValues,
  team: PrefillTeam | null,
  address: PrefillAddress | null,
  categories: PrefillCategory[],
): PrefillValues {
  return {
    ...values,
    ...(team != null && !values.team_name ? {team_name: team.team_name} : {}),
    ...(team != null ? offeredTeamIds(values, team, categories) : {}),
    ...(address != null && !values.address_street_1 ? {
      address_street_1: address.address_street_1 ?? '',
      address_street_2: address.address_street_2 ?? '',
      address_locality: address.address_locality ?? '',
      address_region: address.address_region ?? '',
      address_postal_code: address.address_postal_code ?? '',
      address_country: address.address_country ?? '',
    } : {}),
  }
}

/**
 * One-line summary for a collapsed, completed Team Address section.
 */
export function addressSummary (values: {address_street_1?: string, address_locality?: string, address_region?: string}): string {
  return [values.address_street_1, values.address_locality, values.address_region]
    .filter((part): part is string => part != null && part !== '')
    .join(', ')
}

/**
 * Summary for the Tournament Selection section — it never collapses to a
 * "completed" state (it's an open-ended multi-select), but this is used as an
 * inline hint once at least one tournament has been checked.
 */
export function tournamentSelectionSummary (count: number): string {
  return `${count} ${pluralize('tournament', count)} selected`
}

function emptyValidations (): RegistrationFormFieldDraft['validations'] {
  return {required: false, min_length: null, max_length: null, min_selected: null, max_selected: null}
}

/**
 * A blank draft field for the "Add field" action. Choice types seed a single
 * empty option so an input is visible to fill in.
 */
export function newRegistrationFieldDraft (type: RegistrationFormFieldType = 'text'): RegistrationFormFieldDraft {
  return {
    key: crypto.randomUUID(),
    id: null,
    type,
    label: '',
    placeholder: '',
    validations: emptyValidations(),
    options: fieldTypeHasOptions(type) ? [''] : [],
  }
}

/**
 * The built-in fields an Organizer can attach a description to, in the order
 * they appear on the public Team step (ENG-4017).
 */
export const REGISTRATION_FIELD_DESCRIPTION_KEY_OPTIONS: Array<{value: RegistrationFormFieldKey, label: string}> = [
  {value: 'team_name', label: 'Team Name'},
  {value: 'sport', label: 'Sport'},
  {value: 'age', label: 'Age'},
  {value: 'classification', label: 'Classification'},
]

export function registrationFieldDescriptionKeyLabel (key: RegistrationFormFieldKey): string {
  return labelFromOptions(REGISTRATION_FIELD_DESCRIPTION_KEY_OPTIONS, key)
}

/**
 * A blank draft for the "Add" menu — seeded with whichever field was picked,
 * ready for the Organizer to type a description. `field_key` is unique within
 * the drafts list (the menu excludes already-used fields), so it doubles as
 * the draft's own identity — no synthetic key needed.
 */
export function newRegistrationFieldDescriptionDraft (fieldKey: RegistrationFormFieldKey): RegistrationFormFieldDescription {
  return {
    field_key: fieldKey,
    description: '',
  }
}

/**
 * Serialize the builder's drafts into the update payload. Order doesn't
 * matter server-side — descriptions are keyed on `field_key`, not position.
 */
export function buildRegistrationFieldDescriptionsPayload (drafts: RegistrationFormFieldDescription[]): RegistrationFormFieldDescription[] {
  return drafts.map((draft) => ({
    field_key: draft.field_key,
    description: draft.description.trim(),
  }))
}

/**
 * Build an editable draft from a published field, normalizing nullish config
 * into the concrete shapes the builder edits against.
 */
export function registrationFieldDraftFromModel (field: RegistrationFormField): RegistrationFormFieldDraft {
  return {
    key: crypto.randomUUID(),
    id: field.id ?? null,
    type: field.type,
    label: field.label ?? '',
    placeholder: field.placeholder ?? '',
    validations: {...emptyValidations(), ...(field.validations ?? {})},
    options: field.options ?? [],
  }
}

interface RegistrationFieldPayload {
  type: RegistrationFormFieldType
  label: string
  placeholder: string | null
  validations: Record<string, number | boolean>
  options: string[]
}

/**
 * Serialize the builder's drafts into the publish payload, keeping only the
 * validation keys + options each type supports. Order is preserved — the backend
 * derives `position` from it. The server re-normalizes, so this just needs to be
 * valid, not minimal.
 */
export function buildRegistrationFieldsPayload (drafts: RegistrationFormFieldDraft[]): RegistrationFieldPayload[] {
  return drafts.map((draft) => {
    const validations: Record<string, number | boolean> = {}

    SUPPORTED_VALIDATION_KEYS[draft.type].forEach((key) => {
      const value = draft.validations[key]

      if (key === 'required') {
        if (value === true) {
          validations.required = true
        }
      } else if (typeof value === 'number' && !Number.isNaN(value)) {
        validations[key] = value
      }
    })

    return {
      type: draft.type,
      label: draft.label.trim(),
      placeholder: draft.placeholder.trim() === '' ? null : draft.placeholder.trim(),
      validations,
      options: fieldTypeHasOptions(draft.type)
        ? draft.options.map((option) => option.trim()).filter((option) => option !== '')
        : [],
    }
  })
}
